/**
 * GlobalSearch — Cmd+K / Ctrl+K command palette.
 * Opens as a modal overlay with instant-search across departments, runs, and nodes.
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import { Search, X, Hash, Building2, Play } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useObservabilityStore } from '../../store/useObservabilityStore'

type SearchResult = {
  id: string
  kind: 'run' | 'department' | 'node'
  label: string
  sublabel?: string
  status?: string
}

type GlobalSearchProps = {
  open: boolean
  onClose: () => void
  onSelectRun: (runId: string) => void
  departments?: Array<{ id: string; label: string }>
}

const STATUS_COLOURS: Record<string, string> = {
  succeeded: '#4ade80',
  failed:    '#f87171',
  running:   '#38bdf8',
  waiting:   '#fbbf24',
  queued:    '#fbbf24',
}

function statusColour(status?: string): string {
  if (!status) return '#64748b'
  return STATUS_COLOURS[status.toLowerCase()] ?? '#64748b'
}

export function GlobalSearch({ open, onClose, onSelectRun, departments = [] }: GlobalSearchProps) {
  const [query, setQuery] = useState('')
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const historyRuns = useObservabilityStore((s) => s.historyRuns ?? [])

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('')
      setActiveIdx(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Scroll active item into view when activeIdx changes
  useEffect(() => {
    document.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [activeIdx])

  // Build results from query — cap total at 8
  const runResults: SearchResult[] = []
  const deptResults: SearchResult[] = []
  const q = query.toLowerCase().trim()

  if (q) {
    historyRuns
      .filter((r) => r.id.toLowerCase().includes(q) || r.status.toLowerCase().includes(q))
      .slice(0, 5)
      .forEach((r) => runResults.push({ id: r.id, kind: 'run', label: r.id.slice(0, 8), sublabel: r.status, status: r.status }))

    departments
      .filter((d) => d.label.toLowerCase().includes(q))
      .slice(0, 3)
      .forEach((d) => deptResults.push({ id: d.id, kind: 'department', label: d.label }))
  } else {
    historyRuns.slice(0, 6).forEach((r) =>
      runResults.push({ id: r.id, kind: 'run', label: r.id.slice(0, 8), sublabel: r.status, status: r.status })
    )
  }

  // Cap total at 8
  const allResults: SearchResult[] = [...runResults, ...deptResults].slice(0, 8)

  // Re-derive per-group slices respecting cap
  const cappedRunResults  = allResults.filter((r) => r.kind === 'run')
  const cappedDeptResults = allResults.filter((r) => r.kind === 'department')

  // Flat ordered list for keyboard nav index
  const results: SearchResult[] = [...cappedRunResults, ...cappedDeptResults]

  const handleSelect = useCallback((result: SearchResult) => {
    if (result.kind === 'run') {
      onSelectRun(result.id)
    }
    onClose()
  }, [onSelectRun, onClose])

  // Keyboard navigation
  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return }
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); onClose(); return }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length - 1)) }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)) }
      if (e.key === 'Enter' && results[activeIdx]) { handleSelect(results[activeIdx]) }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, results, activeIdx, handleSelect, onClose])

  const kindIcon = (kind: SearchResult['kind']) => {
    if (kind === 'run')        return <Hash size={14} className="text-accent-cyan" />
    if (kind === 'department') return <Building2 size={14} className="text-accent-blue" />
    return <Play size={14} className="text-accent-amber" />
  }

  // Group label
  const runGroupLabel = q ? 'Matching Runs' : 'Recent Runs'

  // Flat index offset for dept results (for aria-selected mapping)
  const deptOffset = cappedRunResults.length

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-40 bg-bg/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            aria-hidden="true"
          />
          {/* Panel */}
          <motion.div
            className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4"
            initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
          >
            <div
              className="w-full max-w-lg rounded-xl bg-bg-elevated border border-[rgba(93,131,185,0.20)] shadow-glass overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-label="Search"
            >
              {/* Input row */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-[rgba(93,131,185,0.12)]">
                <Search size={16} className="text-text-muted flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  role="combobox"
                  aria-expanded={results.length > 0}
                  aria-autocomplete="list"
                  aria-controls="search-results"
                  placeholder="Search runs, departments, nodes…"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setActiveIdx(0) }}
                  className="flex-1 bg-transparent text-text-primary placeholder-text-muted text-sm outline-none font-mono"
                />
                <button onClick={onClose} aria-label="Close search" className="text-text-muted hover:text-text-primary transition-colors">
                  <X size={16} />
                </button>
              </div>

              {/* Results */}
              {results.length > 0 && (
                <ul id="search-results" role="listbox" className="py-1 max-h-72 overflow-y-auto">
                  {/* Run group */}
                  {cappedRunResults.length > 0 && (
                    <>
                      <li className="px-4 pt-3 pb-1 text-[10px] font-bold tracking-[0.12em] uppercase text-[#3a5068] select-none">
                        {runGroupLabel}
                      </li>
                      {cappedRunResults.map((r, i) => (
                        <li
                          key={`run:${r.id}`}
                          role="option"
                          aria-selected={i === activeIdx}
                          className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
                            i === activeIdx ? 'bg-[rgba(34,211,238,0.08)]' : 'hover:bg-[rgba(255,255,255,0.03)]'
                          }`}
                          onClick={() => handleSelect(r)}
                          onMouseEnter={() => setActiveIdx(i)}
                        >
                          {kindIcon(r.kind)}
                          <span className="flex-1 text-sm text-text-primary font-mono">{r.label}</span>
                          {r.sublabel && (
                            <span className="flex items-center gap-1.5 text-xs text-text-muted">
                              <span
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ background: statusColour(r.status) }}
                                aria-hidden="true"
                              />
                              {r.sublabel}
                            </span>
                          )}
                        </li>
                      ))}
                    </>
                  )}

                  {/* Department group */}
                  {cappedDeptResults.length > 0 && (
                    <>
                      <li className="px-4 pt-3 pb-1 text-[10px] font-bold tracking-[0.12em] uppercase text-[#3a5068] select-none">
                        Departments
                      </li>
                      {cappedDeptResults.map((r, i) => {
                        const flatIdx = deptOffset + i
                        return (
                          <li
                            key={`dept:${r.id}`}
                            role="option"
                            aria-selected={flatIdx === activeIdx}
                            className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
                              flatIdx === activeIdx ? 'bg-[rgba(34,211,238,0.08)]' : 'hover:bg-[rgba(255,255,255,0.03)]'
                            }`}
                            onClick={() => handleSelect(r)}
                            onMouseEnter={() => setActiveIdx(flatIdx)}
                          >
                            {kindIcon(r.kind)}
                            <span className="flex-1 text-sm text-text-primary font-mono">{r.label}</span>
                          </li>
                        )
                      })}
                    </>
                  )}
                </ul>
              )}

              {/* Empty state: no results for a query */}
              {results.length === 0 && query && (
                <div className="px-4 py-6 text-center text-sm text-text-muted">No results for "{query}"</div>
              )}

              {/* Empty state: no recent runs and no query */}
              {results.length === 0 && !query && historyRuns.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-[#3a5068]">
                  <p className="font-semibold mb-1">No recent runs</p>
                  <p className="text-xs">Paste a run ID into the header bar to load one</p>
                </div>
              )}

              {/* Footer hint */}
              <div className="flex items-center gap-4 px-4 py-2 border-t border-[rgba(93,131,185,0.08)] text-[11px] text-text-muted">
                <span><kbd className="font-mono">↑↓</kbd> navigate</span>
                <span><kbd className="font-mono">↵</kbd> select</span>
                <span><kbd className="font-mono">Esc / ⌘K</kbd> close</span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
